import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useTranslation } from '../i18n/useTranslation';
import { API_BASE_URL as API_URL } from '../services/api';
import { isWithinRadius, calculateDistance } from '../utils/distance';
import { LoadingCar } from '../components/LoadingCar';
import './PublishRidePage.css';

// Lazy load map components
const MapLocationPicker = lazy(() => import('../components/MapLocationPicker'));
const RouteMap = lazy(() => import('../components/RouteMap'));
const BusRouteVisualizer = lazy(() => import('../components/BusRouteVisualizer'));

// Arrow Left Icon
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

function PublishRidePage() {
  const { eventCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, authData, setAuthData } = useApp();
  const { t } = useTranslation();

  const mode = searchParams.get('mode') || 'offer';
  const isEdit = searchParams.get('edit') === 'true';
  const isOffer = mode === 'offer';
  const isDemonstrations = location.pathname.startsWith('/dems');

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editData, setEditData] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const [maxPayment, setMaxPayment] = useState(0);
  const [suggestedOffers, setSuggestedOffers] = useState([]); // Nearby offers when requesting a ride
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  // Step-based form navigation
  const [step, setStep] = useState(1);
  const totalSteps = isOffer ? 3 : 3; // Step 1: Info, Step 2: Location, Step 3: Timing/Payment

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    gender: '',
    hidePhone: false,
    seats: '3',
    tripType: 'both',
    vehicleType: 'car',
    dropOffDifferent: false,
    pickupLocation: '',
    pickupLat: null,
    pickupLng: null,
    returnLocation: '',
    returnLat: null,
    returnLng: null,
    preference: 'any',
    description: '',
    passengerCount: 1,
    paymentRequired: 'not_required',
    paymentAmount: '',
    paymentMethod: 'cash',
    arrivalTime: '',
    returnTime: '',
    returnTimeFlexible: false,
    // Bus-specific fields (enhanced with nickname and time)
    busPickupLocations: [{ address: '', lat: null, lng: null, nickname: '', time: '' }],
    busDropoffLocations: [{ address: '', lat: null, lng: null, nickname: '', time: '' }],
    allowCustomPickup: false,
    // Additional organizers for public vehicles (name + phone)
    organizers: [],
    // Disable join requests - contact via WhatsApp only
    disableJoinRequest: false,
  });

  // Track if user has manually edited dropoff locations (to prevent auto-overwrite)
  const [dropoffLocationsManuallyEdited, setDropoffLocationsManuallyEdited] = useState(false);

  // Step validation
  const canGoNext = useCallback(() => {
    if (step === 1) {
      // Step 1: Name and phone required
      return formData.name?.trim() && formData.phone?.trim();
    }
    if (step === 2) {
      // Step 2: Location required
      if (formData.vehicleType === 'public') {
        // For buses, at least one pickup location required
        return formData.busPickupLocations.some(loc => loc.address?.trim());
      }
      return formData.pickupLocation?.trim();
    }
    return true;
  }, [step, formData.name, formData.phone, formData.vehicleType, formData.pickupLocation, formData.busPickupLocations]);

  const goNext = useCallback(() => {
    if (step < totalSteps && canGoNext()) {
      setStep(s => s + 1);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step, totalSteps, canGoNext]);

  const goBack = useCallback(() => {
    if (step > 1) {
      setStep(s => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  // OTP verification disabled

  // Fetch event
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_URL}/event/${eventCode}`);
        if (response.ok) {
          const eventData = await response.json();
          setEvent(eventData);
        } else {
          // Demo event with coordinates for Tel Aviv
          setEvent({ 
            event_id: 'demo', 
            event_name: 'Demo Event', 
            event_location: 'Tel Aviv, Israel',
            location_lat: 32.0853,
            location_lng: 34.7818
          });
        }
      } catch {
        setEvent({ 
          event_id: 'demo', 
          event_name: 'Demo Event', 
          event_location: 'Tel Aviv, Israel',
          location_lat: 32.0853,
          location_lng: 34.7818
        });
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventCode]);

  // Pre-fill from auth
  useEffect(() => {
    if (authData && !isEdit) {
      setFormData(prev => ({
        ...prev,
        name: authData.name || '',
        phone: authData.phone || '',
        email: authData.email || '',
        gender: authData.gender || '',
      }));
    }
  }, [authData, isEdit]);

  // Load edit data if in edit mode
  useEffect(() => {
    if (isEdit) {
      const savedEditData = localStorage.getItem('editRideData');
      if (savedEditData) {
        try {
          const parsed = JSON.parse(savedEditData);
          setEditData(parsed);
          const item = parsed.item;
          const location = item.locations?.[0];
          
          // Populate form with existing data
          setFormData(prev => ({
            ...prev,
            name: item.name || item.driver_name || '',
            phone: item.phone || item.driver_phone || '',
            email: item.email || item.driver_email || '',
            gender: item.gender || '',
            seats: String(item.total_seats || 3),
            description: item.description || '',
            tripType: item.trip_type || 'both',
            pickupLocation: location?.location_address || '',
            pickupLat: location?.location_lat || location?.lat || null,
            pickupLng: location?.location_lng || location?.lng || null,
            preference: item.preference || 'any',
            hidePhone: item.hide_phone || false,
            vehicleType: item.vehicle_type || 'car',
            paymentRequired: item.payment_required || 'not_required',
            paymentAmount: item.payment_amount ? String(item.payment_amount) : '',
            paymentMethod: item.payment_method || 'cash',
            arrivalTime: item.arrival_time || '',
            returnTime: item.return_time || '',
            returnTimeFlexible: item.return_time_flexible || false,
            allowCustomPickup: item.allow_custom_pickup || false,
            organizers: item.organizers || [],
            disableJoinRequest: item.disable_join_request || false,
            passengerCount: item.passenger_count || 1,
          }));
          
          // For bus routes, load all pickup locations
          if (item.vehicle_type === 'public' && item.locations) {
            const pickups = item.locations.filter(l => l.location_type === 'pickup' || l.trip_direction === 'going');
            const dropoffs = item.locations.filter(l => l.location_type === 'dropoff' || l.trip_direction === 'return');
            
            if (pickups.length > 0) {
              setFormData(prev => ({
                ...prev,
                busPickupLocations: pickups.map(l => ({
                  address: l.location_address || '',
                  lat: l.location_lat || l.lat || null,
                  lng: l.location_lng || l.lng || null,
                  nickname: l.nickname || '',
                  time: l.stop_time || ''
                }))
              }));
            }
            
            if (dropoffs.length > 0) {
              setFormData(prev => ({
                ...prev,
                busDropoffLocations: dropoffs.map(l => ({
                  address: l.location_address || '',
                  lat: l.location_lat || l.lat || null,
                  lng: l.location_lng || l.lng || null,
                  nickname: l.nickname || '',
                  time: l.stop_time || ''
                }))
              }));
            }
          }
          
          // Clear localStorage after loading
          localStorage.removeItem('editRideData');
        } catch (e) {
          console.error('Failed to parse edit data:', e);
        }
      }
    }
  }, [isEdit]);

  // Set default arrival time to event start time, return time to event end time
  useEffect(() => {
    if (event?.event_time) {
      setFormData(prev => {
        const updates = {};
        // Set arrival time to event start if not already set
        if (!prev.arrivalTime) {
          updates.arrivalTime = event.event_time;
        }
        // Set return time to event end if not already set
        if (!prev.returnTime && event.event_end_time) {
          updates.returnTime = event.event_end_time;
        }
        if (Object.keys(updates).length > 0) {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }
  }, [event]);

  // Auto-generate return stops from going stops (for buses only, and only if not manually edited)
  useEffect(() => {
    if (formData.vehicleType === 'public' && 
        formData.tripType === 'both' && 
        !dropoffLocationsManuallyEdited &&
        formData.busPickupLocations.length > 0) {
      // Check if at least one pickup location has an address
      const hasValidPickups = formData.busPickupLocations.some(loc => loc.address && loc.address.trim());
      
      if (hasValidPickups) {
        // Reverse the pickup locations to create return route
        const reversedDropoffs = [...formData.busPickupLocations]
          .reverse()
          .map(loc => ({
            address: loc.address,
            lat: loc.lat,
            lng: loc.lng,
            nickname: loc.nickname || '',
            time: '' // Clear time for return stops (user can set manually)
          }));
        
        // Only update if dropoff locations are empty or different
        const currentDropoffs = formData.busDropoffLocations;
        const needsUpdate = currentDropoffs.length === 0 || 
          currentDropoffs.length !== reversedDropoffs.length ||
          currentDropoffs.some((loc, i) => loc.address !== reversedDropoffs[i]?.address);
        
        if (needsUpdate) {
          setFormData(prev => ({
            ...prev,
            busDropoffLocations: reversedDropoffs
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.busPickupLocations, formData.vehicleType, formData.tripType, dropoffLocationsManuallyEdited]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    // Reset manual edit flag when vehicle type or trip type changes
    if (name === 'vehicleType' || name === 'tripType') {
      setDropoffLocationsManuallyEdited(false);
    }
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLocationSelect = useCallback((location) => {
    console.log('📍 handleLocationSelect called with:', location);
    setFormData(prev => {
      const updated = {
        ...prev,
        pickupLocation: location.address,
        pickupLat: location.lat,
        pickupLng: location.lng,
      };
      console.log('📍 Updated formData with pickup location:', { 
        pickupLocation: updated.pickupLocation,
        pickupLat: updated.pickupLat,
        pickupLng: updated.pickupLng
      });
      return updated;
    });
  }, []);

  const handleReturnLocationSelect = useCallback((location) => {
    setFormData(prev => ({
      ...prev,
      returnLocation: location.address,
      returnLat: location.lat,
      returnLng: location.lng,
    }));
  }, []);

  // Bus pickup location handlers
  const addBusPickupLocation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      busPickupLocations: [...prev.busPickupLocations, { address: '', lat: null, lng: null, nickname: '', time: '' }]
    }));
  }, []);

  const removeBusPickupLocation = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      busPickupLocations: prev.busPickupLocations.filter((_, i) => i !== index)
    }));
  }, []);

  const updateBusPickupLocation = useCallback((index, location) => {
    setFormData(prev => ({
      ...prev,
      busPickupLocations: prev.busPickupLocations.map((loc, i) =>
        i === index ? { ...loc, address: location.address, lat: location.lat, lng: location.lng } : loc
      )
    }));
  }, []);

  const updateBusPickupField = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      busPickupLocations: prev.busPickupLocations.map((loc, i) =>
        i === index ? { ...loc, [field]: value } : loc
      )
    }));
  }, []);

  // Bus dropoff location handlers
  const addBusDropoffLocation = useCallback(() => {
    setDropoffLocationsManuallyEdited(true);
    setFormData(prev => ({
      ...prev,
      busDropoffLocations: [...prev.busDropoffLocations, { address: '', lat: null, lng: null, nickname: '', time: '' }]
    }));
  }, []);

  const removeBusDropoffLocation = useCallback((index) => {
    setDropoffLocationsManuallyEdited(true);
    setFormData(prev => ({
      ...prev,
      busDropoffLocations: prev.busDropoffLocations.filter((_, i) => i !== index)
    }));
  }, []);

  const updateBusDropoffLocation = useCallback((index, location) => {
    setDropoffLocationsManuallyEdited(true);
    setFormData(prev => ({
      ...prev,
      busDropoffLocations: prev.busDropoffLocations.map((loc, i) =>
        i === index ? { ...loc, address: location.address, lat: location.lat, lng: location.lng } : loc
      )
    }));
  }, []);

  const updateBusDropoffField = useCallback((index, field, value) => {
    setDropoffLocationsManuallyEdited(true);
    setFormData(prev => ({
      ...prev,
      busDropoffLocations: prev.busDropoffLocations.map((loc, i) =>
        i === index ? { ...loc, [field]: value } : loc
      )
    }));
  }, []);

  // Organizers handlers for public vehicles
  const addOrganizer = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      organizers: [...prev.organizers, { name: '', phone: '' }]
    }));
  }, []);

  const removeOrganizer = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      organizers: prev.organizers.filter((_, i) => i !== index)
    }));
  }, []);

  const updateOrganizer = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      organizers: prev.organizers.map((org, i) =>
        i === index ? { ...org, [field]: value } : org
      )
    }));
  }, []);

  const handleRouteCalculated = useCallback((routeInfo) => {
    if (routeInfo?.distance) {
      const distance = typeof routeInfo.distance === 'string'
        ? parseFloat(routeInfo.distance)
        : routeInfo.distance;
      setRouteDistance(distance);
      setMaxPayment(Math.ceil(distance * 0.7 * 1.5));
    }
    if (routeInfo?.duration) {
      setRouteDuration(routeInfo.duration);
    }
  }, []);

  // ⚠️ OTP DISABLED - Auto login/register and publish directly
  const autoLoginAndPublish = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: formData.phone,
          name: formData.name,
          email: formData.email,
          gender: formData.gender
        })
      });
      const data = await response.json();
      
      if (response.ok && data.token) {
        // Auto-login successful - save auth and publish
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authAccount', JSON.stringify(data.account));
        setAuthData(data.account);
        showToast('Welcome!', 'success');
        
        // Now publish the ride
        await publishRide(data.token);
      } else if (data.requires_registration) {
        // Need more info - but we already have it, try again with all fields
        showToast('Please fill in all your details', 'error');
      } else {
        throw new Error(data.message || 'Failed to login');
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Actual publish function
  const publishRide = async (token) => {
    setSubmitting(true);
    try {
      const endpoint = isOffer ? 'carpool/offer' : 'carpool/request';

      // Build locations array based on trip type and vehicle type
      const locations = [];

      // For buses (public vehicles), use the multiple pickup/dropoff locations
      if (isOffer && formData.vehicleType === 'public') {
        // Add all bus pickup locations
        formData.busPickupLocations.forEach((loc, index) => {
          if (loc.address) {
            locations.push({
              location_address: loc.address,
              lat: loc.lat,
              lng: loc.lng,
              trip_direction: 'going',
              time_type: loc.time ? 'specific' : 'flexible',
              location_type: 'pickup',
              nickname: loc.nickname || null,
              stop_time: loc.time || null,
              stop_order: index + 1,
            });
          }
        });
        // Add all bus dropoff locations
        formData.busDropoffLocations.forEach((loc, index) => {
          if (loc.address) {
            locations.push({
              location_address: loc.address,
              lat: loc.lat,
              lng: loc.lng,
              trip_direction: 'return',
              time_type: loc.time ? 'specific' : 'flexible',
              location_type: 'dropoff',
              nickname: loc.nickname || null,
              stop_time: loc.time || null,
              stop_order: index + 1,
            });
          }
        });
      } else {
        // For cars, use the single location logic
        if (formData.tripType === 'going' || formData.tripType === 'both') {
          locations.push({
            location_address: formData.pickupLocation,
            lat: formData.pickupLat,
            lng: formData.pickupLng,
            trip_direction: 'going',
            time_type: 'flexible',
            location_type: 'pickup',
          });
        }
        if (formData.tripType === 'return' || formData.tripType === 'both') {
          // If drop off is different, use returnLocation for return trip
          const returnAddr = formData.dropOffDifferent && formData.returnLocation
            ? formData.returnLocation : formData.pickupLocation;
          const returnLat = formData.dropOffDifferent && formData.returnLat
            ? formData.returnLat : formData.pickupLat;
          const returnLng = formData.dropOffDifferent && formData.returnLng
            ? formData.returnLng : formData.pickupLng;
          locations.push({
            location_address: returnAddr,
            lat: returnLat,
            lng: returnLng,
            trip_direction: 'return',
            time_type: formData.returnTimeFlexible ? 'flexible' : 'specific',
            location_type: 'dropoff',
          });
        }
      }

      const payload = {
        event_id: event.event_id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        gender: formData.gender,
        hide_phone: formData.hidePhone,
        trip_type: formData.tripType,
        preference: formData.preference,
        description: formData.description,
        privacy: 'public',
        arrival_time: formData.arrivalTime || null,
        return_time: formData.returnTimeFlexible ? null : (formData.returnTime || null),
        return_time_flexible: formData.returnTimeFlexible,
        locations,
      };

      if (isOffer) {
        payload.total_seats = parseInt(formData.seats);
        payload.vehicle_type = formData.vehicleType;
        payload.payment_required = formData.paymentRequired;
        payload.payment_amount = formData.paymentRequired === 'obligatory'
          ? parseFloat(formData.paymentAmount) || 0
          : null;
        payload.payment_method = formData.paymentRequired !== 'not_required'
          ? formData.paymentMethod
          : null;
        // Bus-specific: allow custom pickup and organizers
        if (formData.vehicleType === 'public') {
          payload.allow_custom_pickup = formData.allowCustomPickup;
          payload.disable_join_request = formData.disableJoinRequest;
          // Include additional organizers (filter out empty entries)
          const validOrganizers = formData.organizers.filter(org => org.name && org.phone);
          if (validOrganizers.length > 0) {
            payload.organizers = validOrganizers;
          }
        }
      } else {
        payload.passenger_count = formData.passengerCount || 1;
      }

      // Determine URL and method based on edit mode
      let url = `${API_URL}/${endpoint}`;
      let method = 'POST';
      
      if (isEdit && editData) {
        // Use PUT for updating existing offer/request
        url = isOffer 
          ? `${API_URL}/carpool/offer/${editData.editId}`
          : `${API_URL}/carpool/request/${editData.editId}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const successMsg = isEdit 
          ? (t('publishRide.updateSuccess') || 'تم التحديث بنجاح!')
          : (isOffer ? t('publishRide.ridePublished') : t('publishRide.requestSent'));
        showToast(successMsg, 'success');
        navigate(isDemonstrations ? `/dems/event/${eventCode}` : `/event/${eventCode}`);
      } else {
        const data = await response.json();
        throw new Error(data.message || (isEdit ? t('publishRide.updateFailed') : t('publishRide.failedToPublish')));
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Check for nearby offers when user is requesting a ride
  const checkForNearbyOffers = useCallback(async () => {
    console.log('🔍 checkForNearbyOffers called with:', { isOffer, event_id: event?.event_id, formData: {
      pickupLocation: formData.pickupLocation,
      pickupLat: formData.pickupLat,
      pickupLng: formData.pickupLng,
      vehicleType: formData.vehicleType
    }});
    
    if (isOffer || !event?.event_id) {
      console.log('⏭️ Skipping check: isOffer=', isOffer, 'event_id=', event?.event_id);
      return false; // Only for requests
    }
    
    // Get request pickup location - for requests, always use regular pickup location
    let requestLat, requestLng;
    if (formData.pickupLat && formData.pickupLng) {
      requestLat = parseFloat(formData.pickupLat);
      requestLng = parseFloat(formData.pickupLng);
      console.log('📍 Parsed coordinates:', { requestLat, requestLng, raw: { lat: formData.pickupLat, lng: formData.pickupLng } });
    } else {
      console.log('⚠️ No pickup coordinates in formData:', { pickupLat: formData.pickupLat, pickupLng: formData.pickupLng });
    }
    
    if (!requestLat || !requestLng || isNaN(requestLat) || isNaN(requestLng)) {
      console.log('❌ No valid pickup location coordinates found for request:', { requestLat, requestLng, pickupLat: formData.pickupLat, pickupLng: formData.pickupLng });
      return false;
    }
    
    console.log('🔍 Checking for nearby offers at:', requestLat, requestLng);
    
    try {
      // Fetch offers for this event
      const response = await fetch(`${API_URL}/event/${eventCode}/offers`);
      const data = await response.json();
      const offers = data.offers || [];
      
      console.log('📦 Fetched offers:', offers.length);
      if (offers.length === 0) {
        console.log('No offers found for this event');
        return false;
      }
      
      // Find offers within 5km radius (increased to 10km for testing, can reduce later)
      const nearby = offers.filter(offer => {
        const confirmed = offer.confirmed_passengers?.reduce((sum, p) => sum + (parseInt(p.passenger_count) || 1), 0) || 0;
        const total = offer.total_seats || 0;
        const available = total - confirmed;
        const isFull = available <= 0 || (offer.available_seats !== undefined && Number(offer.available_seats) <= 0);
        if (isFull) {
          return false;
        }

        if (!offer.locations || offer.locations.length === 0) {
          console.log('❌ Offer has no locations:', offer.offer_id);
          return false;
        }
        
        console.log(`🔎 Checking offer ${offer.offer_id} (${offer.vehicle_type}):`, offer.locations);
        
        // For buses, check all pickup locations
        if (offer.vehicle_type === 'public') {
          const matches = offer.locations.some(loc => {
            // Handle both lat/lng and location_lat/location_lng formats
            const offerLat = parseFloat(loc.lat || loc.location_lat);
            const offerLng = parseFloat(loc.lng || loc.location_lng);
            
            if ((loc.trip_direction === 'going' || loc.location_type === 'pickup') && !isNaN(offerLat) && !isNaN(offerLng)) {
              const distance = isWithinRadius(requestLat, requestLng, offerLat, offerLng, 10); // Increased radius
              const actualDistance = calculateDistance(requestLat, requestLng, offerLat, offerLng);
              console.log(`📍 Bus location "${loc.location_address || loc.nickname}": ${actualDistance.toFixed(2)}km away, within 10km = ${distance}`);
              return distance;
            }
            return false;
          });
          if (matches) {
            console.log('✅ Offer matches!');
          }
          return matches;
        } else {
          // For cars, check all locations (prioritize "going" direction)
          // Helper function to get lat/lng from location (handles both formats)
          const getLocationCoords = (loc) => {
            const lat = loc.lat || loc.location_lat;
            const lng = loc.lng || loc.location_lng;
            return { lat, lng };
          };
          
          // First, try to find a "going" location
          let goingLoc = offer.locations.find(loc => {
            const coords = getLocationCoords(loc);
            return loc.trip_direction === 'going' && coords.lat && coords.lng;
          });
          if (!goingLoc) {
            // If no "going" location, check all locations
            goingLoc = offer.locations.find(loc => {
              const coords = getLocationCoords(loc);
              return coords.lat && coords.lng;
            });
          }
          
          if (goingLoc) {
            const coords = getLocationCoords(goingLoc);
            const offerLat = parseFloat(coords.lat);
            const offerLng = parseFloat(coords.lng);
            if (isNaN(offerLat) || isNaN(offerLng)) {
              console.log('⚠️ Invalid coordinates in offer location:', goingLoc);
              return false;
            }
            const distance = isWithinRadius(requestLat, requestLng, offerLat, offerLng, 10); // Increased radius
            const actualDistance = calculateDistance(requestLat, requestLng, offerLat, offerLng);
            console.log(`📍 Car location "${goingLoc.location_address || goingLoc.nickname}" (${goingLoc.trip_direction || 'unknown'}): ${actualDistance.toFixed(2)}km away, within 10km = ${distance}`);
            if (distance) {
              console.log('✅ Offer matches!');
            }
            return distance;
          } else {
            console.log('⚠️ No valid location found in car offer:', offer.offer_id);
            // Fallback: check all locations
            const matches = offer.locations.some(loc => {
              const coords = getLocationCoords(loc);
              if (coords.lat && coords.lng) {
                const offerLat = parseFloat(coords.lat);
                const offerLng = parseFloat(coords.lng);
                if (!isNaN(offerLat) && !isNaN(offerLng)) {
                  const distance = isWithinRadius(requestLat, requestLng, offerLat, offerLng, 10);
                  const actualDistance = calculateDistance(requestLat, requestLng, offerLat, offerLng);
                  console.log(`📍 Car location "${loc.location_address || loc.nickname}" (${loc.trip_direction || 'unknown'}): ${actualDistance.toFixed(2)}km away, within 10km = ${distance}`);
                  return distance;
                }
              }
              return false;
            });
            if (matches) {
              console.log('✅ Offer matches!');
            }
            return matches;
          }
        }
      });
      
      console.log('🎯 Nearby offers found:', nearby.length);
      if (nearby.length > 0) {
        console.log('✅ Setting suggested offers:', nearby.map(o => ({ id: o.offer_id, name: o.driver_name || o.name })));
        setSuggestedOffers(nearby);
        setShowSuggestionModal(true);
        return true; // Found suggestions
      } else {
        console.log('❌ No nearby offers found');
      }
    } catch (err) {
      console.error('❌ Error checking for nearby offers:', err);
    }
    return false; // No suggestions found
  }, [isOffer, event, eventCode, formData]);

  // Handle publish button click
  const handlePublish = async () => {
    console.log('🚀 handlePublish called, isOffer:', isOffer, 'formData:', {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      pickupLocation: formData.pickupLocation,
      pickupLat: formData.pickupLat,
      pickupLng: formData.pickupLng,
      event_id: event?.event_id
    });
    
    // Validate form
    if (!formData.name || !formData.phone || !formData.gender) {
      showToast(t('publishRide.fillRequiredFields'), 'error');
      return;
    }
    // Validate location based on vehicle type
    if (isOffer && formData.vehicleType === 'public') {
      // For buses, check if at least one pickup location is filled
      const hasPickup = formData.busPickupLocations.some(loc => loc.address && loc.address.trim());
      if (!hasPickup) {
        showToast(t('publishRide.selectPickupLocation'), 'error');
        return;
      }
    } else {
      // For cars and requests, check single pickup location
      if (!formData.pickupLocation) {
        showToast(t('publishRide.selectPickupLocation'), 'error');
        return;
      }
    }
    if (!formData.email) {
      showToast(t('publishRide.enterEmail'), 'error');
      return;
    }

    // If requesting a ride, check for nearby offers first
    if (!isOffer) {
      console.log('📞 Requesting a ride - checking for nearby offers...');
      console.log('📞 Form data at check time:', { 
        pickupLocation: formData.pickupLocation,
        pickupLat: formData.pickupLat,
        pickupLng: formData.pickupLng 
      });
      const hasSuggestions = await checkForNearbyOffers();
      console.log('📞 checkForNearbyOffers returned:', hasSuggestions);
      if (hasSuggestions) {
        console.log('✅ Suggestions found - showing modal, not proceeding with request');
        // Don't proceed - show suggestion modal instead
        return;
      } else {
        console.log('❌ No suggestions found - proceeding with request creation');
      }
    }

    const token = localStorage.getItem('authToken');
    
    // If user is logged in, publish directly
    if (authData && token) {
      await publishRide(token);
    } else {
      // ⚠️ OTP DISABLED - Auto login and publish directly
      await autoLoginAndPublish();
    }
  };

  // Proceed with request after user dismisses suggestions
  const proceedWithRequest = async () => {
    setShowSuggestionModal(false);
    const token = localStorage.getItem('authToken');
    
    if (authData && token) {
      await publishRide(token);
    } else {
      await autoLoginAndPublish();
    }
  };

  const goBackToEvent = useCallback(() => {
    navigate(`/event/${eventCode}`);
  }, [navigate, eventCode]);

  // Loading State - use car gif
  if (loading) {
    return (
      <div className="publish-ride-page">
        <div className="pr-loading pr-loading-car">
          <LoadingCar />
          <p className="pr-loading-text">{t('publishRide.loadingEvent')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="publish-ride-page">
      {/* Submitting Overlay */}
      {submitting && (
        <div className="pr-submitting">
          <LoadingCar />
          <p className="pr-submitting-text">
            {isOffer ? t('publishRide.publishingRide') : t('publishRide.sendingRequest')}
          </p>
          <p className="pr-submitting-subtext">{t('publishRide.pleaseWait')}</p>
        </div>
      )}

      {/* Header */}
      <header className="pr-header">
        <div className="pr-header-inner">
          <button className="pr-back-btn" onClick={step > 1 ? goBack : goBackToEvent} aria-label="Go back">
            <ArrowLeftIcon />
          </button>
          
          <h1 className="pr-header-title">
            {isEdit 
              ? (isOffer ? (t('publishRide.editOffer') || '✏️ تعديل العرض') : (t('publishRide.editRequest') || '✏️ تعديل الطلب'))
              : (isOffer ? t('publishRide.offerRide') : t('publishRide.requestRide'))
            }
          </h1>
          
          <div className="pr-header-spacer"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pr-content">
        {/* Destination Card */}
        <div className="pr-destination">
          <div className="pr-destination-icon">📍</div>
          <div className="pr-destination-info">
            <span className="pr-destination-label">{event?.event_name}</span>
            <div className="pr-destination-address">{event?.event_location}</div>
          </div>
        </div>

        {/* Step Progress Indicator */}
        <div className="pr-step-progress">
          <div className="pr-step-bar">
            <div className="pr-step-bar-fill" style={{ width: `${(step / totalSteps) * 100}%` }} />
          </div>
          <div className="pr-step-labels">
            <span className={step >= 1 ? 'active' : ''}>{t('publishRide.stepInfo') || 'Info'}</span>
            <span className={step >= 2 ? 'active' : ''}>{t('publishRide.stepLocation') || 'Location'}</span>
            <span className={step >= 3 ? 'active' : ''}>{isOffer ? (t('publishRide.stepDetails') || 'Details') : (t('publishRide.stepTiming') || 'Timing')}</span>
          </div>
        </div>

        {/* STEP 1: Basic Info + Vehicle Type (for offers) */}
        {step === 1 && (
          <>
            {/* Section 0: Vehicle Type (First!) - Only for offers */}
            {isOffer && (
          <section className="pr-section">
            <div className="pr-section-header">
              <div className="pr-section-icon">🚗</div>
              <div className="pr-section-title">
                <h3>{t('publishRide.vehicleType') || 'نوع المركبة'}</h3>
                <p>{t('publishRide.selectVehicleType') || 'اختر نوع المركبة'}</p>
              </div>
            </div>
            <div className="pr-section-body">
              <div className="pr-options">
                <button
                  type="button"
                  className={`pr-option ${formData.vehicleType === 'car' ? 'selected' : ''}`}
                  onClick={() => {
                    updateField('vehicleType', 'car');
                    // Reset seats to car default if changing from bus
                    if (parseInt(formData.seats) > 6) {
                      updateField('seats', '3');
                    }
                    // Clear organizers when switching to car
                    updateField('organizers', []);
                  }}
                >
                  <span className="pr-option-icon">🚗</span>
                  {t('publishRide.car') || 'سيارة عادية'}
                </button>
                <button
                  type="button"
                  className={`pr-option ${formData.vehicleType === 'public' ? 'selected' : ''}`}
                  onClick={() => {
                    updateField('vehicleType', 'public');
                    // Set default seats to 10 for public vehicle
                    updateField('seats', '10');
                  }}
                >
                  <span className="pr-option-icon">🚌</span>
                  {t('publishRide.publicVehicle') || 'مركبة عمومية'}
                </button>
              </div>

              {/* Additional Organizers - Only for public vehicles */}
              {formData.vehicleType === 'public' && (
                <div className="pr-organizers-section" style={{ marginTop: '20px' }}>
                  <div className="pr-organizers-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label className="pr-label" style={{ margin: 0 }}>
                      {t('publishRide.additionalOrganizers') || 'منظمون إضافيون'}
                      <span style={{ fontSize: '12px', color: '#888', marginRight: '8px' }}>
                        ({t('common.optional') || 'اختياري'})
                      </span>
                    </label>
                    <button
                      type="button"
                      className="pr-add-organizer-btn"
                      onClick={addOrganizer}
                      style={{
                        background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        fontSize: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                      }}
                    >
                      +
                    </button>
                  </div>
                  
                  {formData.organizers.length > 0 && (
                    <div className="pr-organizers-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.organizers.map((org, index) => (
                        <div key={index} className="pr-organizer-item" style={{
                          display: 'flex',
                          gap: '8px',
                          padding: '12px',
                          background: 'rgba(99, 102, 241, 0.05)',
                          borderRadius: '12px',
                          border: '1px solid rgba(99, 102, 241, 0.2)',
                          alignItems: 'center'
                        }}>
                          <div style={{ flex: 1, display: 'flex', gap: '8px' }}>
                            <input
                              type="text"
                              className="pr-input"
                              value={org.name}
                              onChange={(e) => updateOrganizer(index, 'name', e.target.value)}
                              placeholder={t('publishRide.organizerName') || 'اسم المنظم'}
                              style={{ flex: 1 }}
                            />
                            <input
                              type="tel"
                              className="pr-input"
                              value={org.phone}
                              onChange={(e) => updateOrganizer(index, 'phone', e.target.value)}
                              placeholder={t('publishRide.organizerPhone') || 'رقم الهاتف'}
                              style={{ flex: 1 }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeOrganizer(index)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#EF4444',
                              border: 'none',
                              borderRadius: '8px',
                              width: '32px',
                              height: '32px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {formData.organizers.length === 0 && (
                    <p style={{ fontSize: '13px', color: '#888', textAlign: 'center', padding: '16px' }}>
                      {t('publishRide.addOrganizersHint') || 'اضغط على + لإضافة منظمين إضافيين يمكن التواصل معهم'}
                    </p>
                  )}
                </div>
              )}

              {/* Disable Join Request checkbox - WhatsApp only mode */}
              <div className="pr-checkbox-row" style={{ 
                marginTop: '20px', 
                padding: '16px', 
                background: 'rgba(245, 158, 11, 0.1)', 
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <label className="pr-checkbox-label" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.disableJoinRequest}
                    onChange={(e) => setFormData({...formData, disableJoinRequest: e.target.checked})}
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      accentColor: '#F59E0B',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, color: '#92400E', display: 'block' }}>
                      {t('publishRide.disableJoinRequest') || 'تواصل عبر واتساب فقط'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#B45309' }}>
                      {t('publishRide.disableJoinRequestHint') || 'إلغاء زر "طلب الإنضمام" - يمكن للركاب التواصل معك عبر واتساب فقط'}
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </section>
        )}

        {/* Section 1: Personal Info */}
        <section className="pr-section">
          <div className="pr-section-header">
            <div className="pr-section-icon">👤</div>
            <div className="pr-section-title">
              <h3>{t('publishRide.yourInformation')}</h3>
              <p>{t('publishRide.letOthersKnow')}</p>
            </div>
          </div>
          <div className="pr-section-body">
            <div className="pr-row">
              <div className="pr-field">
                <label className="pr-label">
                  {t('publishRide.name')} <span className="pr-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="pr-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('publishRide.namePlaceholder')}
                />
              </div>
              <div className="pr-field">
                <label className="pr-label">
                  {t('publishRide.phone')} <span className="pr-required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="pr-input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('publishRide.phonePlaceholder')}
                />
                <label className="pr-checkbox-label">
                  <input
                    type="checkbox"
                    name="hidePhone"
                    checked={formData.hidePhone}
                    onChange={handleChange}
                  />
                  <span className="pr-checkbox-text">
                    {t('publishRide.hidePhone') || 'Hide my phone number until accepted'}
                  </span>
                </label>
              </div>
            </div>

            <div className="pr-field">
              <label className="pr-label">{t('publishRide.email')}</label>
              <input
                type="email"
                name="email"
                className="pr-input"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('publishRide.emailPlaceholder')}
              />
            </div>

            <div className="pr-field">
              <label className="pr-label">
                {t('publishRide.gender')} <span className="pr-required">*</span>
              </label>
              <div className="pr-options">
                <button
                  type="button"
                  className={`pr-option ${formData.gender === 'male' ? 'selected' : ''}`}
                  onClick={() => updateField('gender', 'male')}
                >
                  <span className="pr-option-icon">👨</span>
                  {t('publishRide.male')}
                </button>
                <button
                  type="button"
                  className={`pr-option ${formData.gender === 'female' ? 'selected' : ''}`}
                  onClick={() => updateField('gender', 'female')}
                >
                  <span className="pr-option-icon">👩</span>
                  {t('publishRide.female')}
                </button>
              </div>
            </div>
          </div>
        </section>

            {/* Step 1 Navigation */}
            <div className="pr-step-nav">
              <button type="button" className="pr-step-btn pr-step-btn-next" onClick={goNext} disabled={!canGoNext()}>
                {t('common.next') || 'Next'} →
              </button>
            </div>
          </>
        )}

        {/* STEP 2: Location */}
        {step === 2 && (
          <>
            {/* Section 2: Trip Direction - BEFORE Location */}
            <section className="pr-section">
          <div className="pr-section-header">
            <div className="pr-section-icon">🚗</div>
            <div className="pr-section-title">
              <h3>{t('publishRide.tripDetails')}</h3>
              <p>{t('publishRide.configurePreferences')}</p>
            </div>
          </div>
          <div className="pr-section-body">
            <div className="pr-field">
              <label className="pr-label">{t('publishRide.tripDirection')}</label>
              <div className="pr-options pr-options-wrap">
                {[
                  { value: 'going', icon: '➡️', label: t('publishRide.going') },
                  { value: 'return', icon: '⬅️', label: t('publishRide.return') },
                  { value: 'both', icon: '↔️', label: t('publishRide.both') },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pr-option ${formData.tripType === opt.value ? 'selected' : ''}`}
                    onClick={() => updateField('tripType', opt.value)}
                  >
                    <span className="pr-option-icon">{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Location */}
        <section className="pr-section">
          <div className="pr-section-header">
            <div className="pr-section-icon">📍</div>
            <div className="pr-section-title">
              <h3>{t('publishRide.yourLocation')}</h3>
              <p>{isOffer ? t('publishRide.whereStartFrom') : t('publishRide.wherePickedUp')}</p>
            </div>
          </div>
          <div className="pr-section-body">
            {/* Bus/Public Vehicle - Enhanced Multiple Pickup/Dropoff Locations */}
            {isOffer && formData.vehicleType === 'public' ? (
              <>
                {/* Visual Route Map */}
                <div className="pr-bus-route-map">
                  <Suspense fallback={
                    <div className="pr-map-loading" style={{ height: '280px' }}>
                      <div className="pr-map-loading-spinner" />
                      <p className="pr-map-loading-text">{t('publishRide.loadingMap') || 'Loading map...'}</p>
                    </div>
                  }>
                    <BusRouteVisualizer
                      pickupStops={formData.busPickupLocations}
                      dropoffStops={formData.busDropoffLocations}
                      eventLocation={event ? { lat: event.location_lat, lng: event.location_lng } : null}
                      eventName={event?.event_name}
                      height={280}
                    />
                  </Suspense>
                </div>

                {/* Pickup Stops Timeline - Only show for 'going' or 'both' trips */}
                {(formData.tripType === 'going' || formData.tripType === 'both') && (
                <div className="pr-bus-stops-section">
                  <div className="pr-bus-stops-header">
                    <div className="pr-bus-stops-icon pickup">📍</div>
                    <div className="pr-bus-stops-title">
                      <h4>{t('publishRide.pickupStops') || 'Pickup Stops'}</h4>
                      <p>{t('publishRide.pickupStopsDesc') || 'Where passengers will board'}</p>
                    </div>
                  </div>
                  
                  <div className="pr-bus-timeline">
                    {formData.busPickupLocations.map((loc, index) => (
                      <div key={`pickup-${index}`} className="pr-bus-stop-card">
                        <div className="pr-bus-stop-number">
                          <span className="pr-bus-stop-badge pickup">{index + 1}</span>
                          {index < formData.busPickupLocations.length - 1 && (
                            <div className="pr-bus-stop-line pickup"></div>
                          )}
                        </div>
                        <div className="pr-bus-stop-content">
                          <div className="pr-bus-stop-main">
                            <Suspense fallback={
                              <div className="pr-map-loading" style={{ padding: '16px', borderRadius: '12px' }}>
                                <div className="pr-map-loading-spinner" style={{ width: '24px', height: '24px' }} />
                              </div>
                            }>
                              <MapLocationPicker
                                value={loc.address}
                                onChange={(val) => {
                                  const newLocs = [...formData.busPickupLocations];
                                  newLocs[index] = { ...newLocs[index], address: val };
                                  updateField('busPickupLocations', newLocs);
                                }}
                                onLocationSelect={(location) => updateBusPickupLocation(index, location)}
                                placeholder={t('publishRide.searchStopLocation') || 'Search for stop location...'}
                                compact={true}
                              />
                            </Suspense>
                          </div>
                          
                          {/* Optional fields row */}
                          <div className="pr-bus-stop-extras">
                            <div className="pr-bus-stop-field">
                              <label>
                                <span className="pr-bus-field-icon">🏷️</span>
                                {t('publishRide.stopNickname') || 'Nickname'}
                                <span className="pr-optional-badge">{t('common.optional') || 'Optional'}</span>
                              </label>
                              <input
                                type="text"
                                className="pr-input pr-bus-input"
                                value={loc.nickname || ''}
                                onChange={(e) => updateBusPickupField(index, 'nickname', e.target.value)}
                                placeholder={t('publishRide.stopNicknamePlaceholder') || 'e.g., Central Station'}
                              />
                            </div>
                            <div className="pr-bus-stop-field">
                              <label>
                                <span className="pr-bus-field-icon">🕐</span>
                                {t('publishRide.stopTime') || 'Pickup Time'}
                                <span className="pr-optional-badge">{t('common.optional') || 'Optional'}</span>
                              </label>
                              <input
                                type="time"
                                className="pr-input pr-bus-input pr-time-input"
                                value={loc.time || ''}
                                onChange={(e) => updateBusPickupField(index, 'time', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          {formData.busPickupLocations.length > 1 && (
                            <button
                              type="button"
                              className="pr-bus-stop-remove"
                              onClick={() => removeBusPickupLocation(index)}
                              title={t('publishRide.removeStop') || 'Remove stop'}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    className="pr-bus-add-stop"
                    onClick={addBusPickupLocation}
                  >
                    <span className="pr-bus-add-icon">+</span>
                    {t('publishRide.addPickupStop') || 'Add Pickup Stop'}
                  </button>
                </div>
                )}

                {/* Event Destination Indicator - Only show if both directions */}
                {formData.tripType === 'both' && (
                <div className="pr-bus-event-indicator">
                  <div className="pr-bus-event-line"></div>
                  <div className="pr-bus-event-badge">
                    <span>🎯</span>
                    <span>{event?.event_name || t('publishRide.event') || 'Event'}</span>
                  </div>
                  <div className="pr-bus-event-line"></div>
                </div>
                )}

                {/* Dropoff Stops Timeline - Only show for 'return' or 'both' trips */}
                {(formData.tripType === 'return' || formData.tripType === 'both') && (
                <div className="pr-bus-stops-section">
                  <div className="pr-bus-stops-header">
                    <div className="pr-bus-stops-icon dropoff">📍</div>
                    <div className="pr-bus-stops-title">
                      <h4>{t('publishRide.dropoffStops') || 'Drop-off Stops'}</h4>
                      <p>{t('publishRide.dropoffStopsDesc') || 'Where passengers will be dropped off after the event'}</p>
                    </div>
                  </div>
                  {!dropoffLocationsManuallyEdited && formData.vehicleType === 'public' && formData.tripType === 'both' && formData.busPickupLocations.some(loc => loc.address) && (
                    <div className="pr-auto-generated-notice" style={{ 
                      padding: '8px 12px', 
                      marginBottom: '12px', 
                      background: 'rgba(99, 102, 241, 0.1)', 
                      border: '1px solid rgba(99, 102, 241, 0.3)', 
                      borderRadius: '8px', 
                      fontSize: '12px', 
                      color: '#6366F1' 
                    }}>
                      ✨ {t('publishRide.autoGeneratedReturn') || 'Return stops auto-generated from pickup route. You can edit them below.'}
                    </div>
                  )}
                  
                  <div className="pr-bus-timeline">
                    {formData.busDropoffLocations.map((loc, index) => (
                      <div key={`dropoff-${index}`} className="pr-bus-stop-card">
                        <div className="pr-bus-stop-number">
                          <span className="pr-bus-stop-badge dropoff">{index + 1}</span>
                          {index < formData.busDropoffLocations.length - 1 && (
                            <div className="pr-bus-stop-line dropoff"></div>
                          )}
                        </div>
                        <div className="pr-bus-stop-content">
                          <div className="pr-bus-stop-main">
                            <Suspense fallback={
                              <div className="pr-map-loading" style={{ padding: '16px', borderRadius: '12px' }}>
                                <div className="pr-map-loading-spinner" style={{ width: '24px', height: '24px' }} />
                              </div>
                            }>
                              <MapLocationPicker
                                value={loc.address}
                                onChange={(val) => {
                                  setDropoffLocationsManuallyEdited(true);
                                  const newLocs = [...formData.busDropoffLocations];
                                  newLocs[index] = { ...newLocs[index], address: val };
                                  updateField('busDropoffLocations', newLocs);
                                }}
                                onLocationSelect={(location) => updateBusDropoffLocation(index, location)}
                                placeholder={t('publishRide.searchStopLocation') || 'Search for stop location...'}
                                compact={true}
                              />
                            </Suspense>
                          </div>
                          
                          {/* Optional fields row */}
                          <div className="pr-bus-stop-extras">
                            <div className="pr-bus-stop-field">
                              <label>
                                <span className="pr-bus-field-icon">🏷️</span>
                                {t('publishRide.stopNickname') || 'Nickname'}
                                <span className="pr-optional-badge">{t('common.optional') || 'Optional'}</span>
                              </label>
                              <input
                                type="text"
                                className="pr-input pr-bus-input"
                                value={loc.nickname || ''}
                                onChange={(e) => updateBusDropoffField(index, 'nickname', e.target.value)}
                                placeholder={t('publishRide.stopNicknamePlaceholder') || 'e.g., Central Station'}
                              />
                            </div>
                            <div className="pr-bus-stop-field">
                              <label>
                                <span className="pr-bus-field-icon">🕐</span>
                                {t('publishRide.stopTime') || 'Drop-off Time'}
                                <span className="pr-optional-badge">{t('common.optional') || 'Optional'}</span>
                              </label>
                              <input
                                type="time"
                                className="pr-input pr-bus-input pr-time-input"
                                value={loc.time || ''}
                                onChange={(e) => updateBusDropoffField(index, 'time', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          {formData.busDropoffLocations.length > 1 && (
                            <button
                              type="button"
                              className="pr-bus-stop-remove"
                              onClick={() => removeBusDropoffLocation(index)}
                              title={t('publishRide.removeStop') || 'Remove stop'}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    type="button"
                    className="pr-bus-add-stop dropoff"
                    onClick={addBusDropoffLocation}
                  >
                    <span className="pr-bus-add-icon">+</span>
                    {t('publishRide.addDropoffStop') || 'Add Drop-off Stop'}
                  </button>
                </div>
                )}

                {/* Allow Custom Pickup Checkbox */}
                <div className="pr-bus-option">
                  <label className="pr-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.allowCustomPickup}
                      onChange={(e) => updateField('allowCustomPickup', e.target.checked)}
                    />
                    <span className="pr-checkbox-text">
                      {t('publishRide.allowCustomPickup') || 'Allow passengers to request custom pickup location'}
                    </span>
                  </label>
                </div>
              </>
            ) : (
              <>
                {/* Car - Single Pickup Location */}
                <Suspense fallback={
                  <div className="pr-map-loading">
                    <div className="pr-map-loading-spinner" />
                    <p className="pr-map-loading-text">{t('publishRide.loadingMap')}</p>
                  </div>
                }>
                  <div className="pr-map-container">
                    <MapLocationPicker
                      value={formData.pickupLocation}
                      onChange={(val) => updateField('pickupLocation', val)}
                      onLocationSelect={handleLocationSelect}
                      placeholder={t('publishRide.searchLocation')}
                    />
                  </div>
                </Suspense>

                {/* Drop off is different checkbox - only show for both trips */}
                {formData.tripType === 'both' && (
                  <div className="pr-field" style={{ marginTop: '16px' }}>
                    <label className="pr-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.dropOffDifferent}
                        onChange={(e) => updateField('dropOffDifferent', e.target.checked)}
                      />
                      {t('publishRide.dropOffDifferent') || 'Drop off is different'}
                    </label>
                  </div>
                )}

                {/* Return Location - only when drop off is different */}
                {formData.tripType === 'both' && formData.dropOffDifferent && (
                  <div style={{ marginTop: '16px' }}>
                    <label className="pr-label" style={{ marginBottom: '8px', display: 'block' }}>
                      {t('publishRide.returnLocation') || 'Return Location (Drop-off)'}
                    </label>
                    <Suspense fallback={
                      <div className="pr-map-loading">
                        <div className="pr-map-loading-spinner" />
                        <p className="pr-map-loading-text">{t('publishRide.loadingMap')}</p>
                      </div>
                    }>
                      <div className="pr-map-container">
                        <MapLocationPicker
                          value={formData.returnLocation}
                          onChange={(val) => updateField('returnLocation', val)}
                          onLocationSelect={handleReturnLocationSelect}
                          placeholder={t('publishRide.searchReturnLocation') || 'Search return location...'}
                        />
                      </div>
                    </Suspense>
                  </div>
                )}

                {/* Route Preview */}
                {formData.pickupLat && formData.pickupLng && event?.event_location && (
                  <div style={{ marginTop: '16px' }}>
                    <Suspense fallback={
                      <div className="pr-map-loading">
                        <div className="pr-map-loading-spinner" />
                        <p className="pr-map-loading-text">{t('publishRide.calculatingRoute')}</p>
                      </div>
                    }>
                      <div className="pr-map-container">
                        <RouteMap
                          origin={{
                            address: formData.pickupLocation,
                            lat: formData.pickupLat,
                            lng: formData.pickupLng
                          }}
                          destination={
                            event.location_lat && event.location_lng
                              ? { address: event.event_location, lat: event.location_lat, lng: event.location_lng }
                              : event.event_location
                          }
                          height="180px"
                          showDirections={true}
                          onRouteCalculated={handleRouteCalculated}
                        />
                      </div>
                    </Suspense>

                    {(routeDistance || routeDuration) && (
                      <div className="pr-route-info">
                        {routeDistance && (
                          <>
                            <span className="pr-route-icon">🛣️</span>
                            <span className="pr-route-distance">
                              {typeof routeDistance === 'number' ? `${routeDistance.toFixed(1)} km` : routeDistance}
                            </span>
                          </>
                        )}
                        {routeDuration && (
                          <>
                            <span className="pr-route-separator">•</span>
                            <span className="pr-route-icon">⏱️</span>
                            <span className="pr-route-duration">{routeDuration}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

            {/* Step 2 Navigation */}
            <div className="pr-step-nav">
              <button type="button" className="pr-step-btn pr-step-btn-back" onClick={goBack}>
                ← {t('common.back') || 'Back'}
              </button>
              <button type="button" className="pr-step-btn pr-step-btn-next" onClick={goNext} disabled={!canGoNext()}>
                {t('common.next') || 'Next'} →
              </button>
            </div>
          </>
        )}

        {/* STEP 3: Timing & Payment */}
        {step === 3 && (
          <>
            {/* Section 4: Time & Seats */}
            <section className="pr-section">
              <div className="pr-section-header">
                <div className="pr-section-icon">⏰</div>
                <div className="pr-section-title">
                  <h3>{t('publishRide.timeAndSeats') || 'Time & Seats'}</h3>
                  <p>{t('publishRide.configurePreferences')}</p>
                </div>
              </div>
              <div className="pr-section-body">
                {/* Arrival Time Selection - for going and both */}
                {event?.event_time && (formData.tripType === 'going' || formData.tripType === 'both') && (
              <div className="pr-field">
                <label className="pr-label">
                  {t('publishRide.arrivalTime') || 'Preferred Arrival Time'}
                </label>
                <div className="pr-arrival-time">
                  <span className="pr-arrival-hint">
                    {t('publishRide.eventTime') || 'Event'}: {event.event_time}{event.event_end_time && ` - ${event.event_end_time}`}
                  </span>
                  <input
                    type="time"
                    name="arrivalTime"
                    className="pr-input pr-time-input"
                    value={formData.arrivalTime}
                    onChange={handleChange}
                    min={event.event_time}
                    max={event.event_end_time || undefined}
                  />
                </div>
              </div>
            )}

            {/* Return Time Selection - for return and both */}
            {(formData.tripType === 'return' || formData.tripType === 'both') && (
              <div className="pr-field">
                <label className="pr-label">
                  {t('publishRide.returnTime') || 'Return Time'}
                </label>
                <div className="pr-return-time-row">
                  <input
                    type="time"
                    name="returnTime"
                    className="pr-input pr-time-input"
                    value={formData.returnTime}
                    onChange={handleChange}
                    disabled={formData.returnTimeFlexible}
                  />
                  <label className="pr-checkbox-label pr-flexible-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.returnTimeFlexible}
                      onChange={(e) => updateField('returnTimeFlexible', e.target.checked)}
                    />
                    {t('publishRide.flexible') || 'Flexible'}
                  </label>
                </div>
              </div>
            )}

            <div className="pr-field">
              <label className="pr-label">
                {isOffer ? t('publishRide.availableSeats') : t('publishRide.passengers')}
              </label>
              {isOffer && formData.vehicleType === 'public' ? (
                /* Public vehicle - dropdown 6-50 */
                <select
                  className="pr-input pr-select"
                  value={formData.seats}
                  onChange={(e) => updateField('seats', e.target.value)}
                >
                  {Array.from({ length: 45 }, (_, i) => i + 6).map(n => (
                    <option key={n} value={String(n)}>{n}</option>
                  ))}
                </select>
              ) : (
                /* Car - buttons 1-6 */
                <div className="pr-seats">
                  {(isOffer ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4]).map(n => (
                    <button
                      key={n}
                      type="button"
                      className={`pr-seat ${
                        (isOffer ? formData.seats === String(n) : formData.passengerCount === n)
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => updateField(
                        isOffer ? 'seats' : 'passengerCount',
                        isOffer ? String(n) : n
                      )}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="pr-field">
              <label className="pr-label">{t('publishRide.passengerPreference')}</label>
              <div className="pr-options">
                {[
                  { value: 'any', icon: '👥', label: t('publishRide.anyone') },
                  { value: 'male', icon: '👨', label: t('publishRide.men') },
                  { value: 'female', icon: '👩', label: t('publishRide.women') },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pr-option ${formData.preference === opt.value ? 'selected' : ''}`}
                    onClick={() => updateField('preference', opt.value)}
                  >
                    <span className="pr-option-icon">{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Payment (Offers Only) */}
        {isOffer && (
          <section className="pr-section">
            <div className="pr-section-header">
              <div className="pr-section-icon">💰</div>
            <div className="pr-section-title">
              <h3>{t('publishRide.payment')}</h3>
              <p>{t('publishRide.setPaymentPreferences')}</p>
            </div>
          </div>
          <div className="pr-section-body">
            <div className="pr-payment-types">
              {[
                { value: 'not_required', icon: '🆓', label: t('publishRide.freeRide') },
                { value: 'optional', icon: '💝', label: t('publishRide.tipWelcome') },
                { value: 'obligatory', icon: '💵', label: t('publishRide.required') },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`pr-payment-type ${formData.paymentRequired === opt.value ? 'selected' : ''}`}
                  onClick={() => updateField('paymentRequired', opt.value)}
                >
                  <span className="pr-payment-type-icon">{opt.icon}</span>
                  <span className="pr-payment-type-label">{opt.label}</span>
                </button>
              ))}
            </div>

              {formData.paymentRequired !== 'not_required' && (
                <div className="pr-payment-details">
                  {/* Only show amount for obligatory payment (not for tips) */}
                  {formData.paymentRequired === 'obligatory' && (
                    <div className="pr-field">
                      <label className="pr-label">{t('publishRide.amount')}</label>
                      <div className="pr-price-wrapper">
                        <span className="pr-price-currency">₪</span>
                        <input
                          type="number"
                          name="paymentAmount"
                          className="pr-price-input"
                          value={formData.paymentAmount}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      {maxPayment > 0 && (
                        <div className="pr-price-hint">
                          {t('publishRide.suggestedMax')}: 
                          <span className="pr-price-suggested">₪{maxPayment}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pr-field">
                    <label className="pr-label">{t('publishRide.paymentMethod')}</label>
                    <div className="pr-method-options">
                      {[
                        { value: 'cash', icon: '💵', label: t('publishRide.cash') },
                        { value: 'bit', icon: '💳', label: t('publishRide.bit') },
                        { value: 'paybox', icon: '📱', label: t('publishRide.paybox') },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`pr-method-btn ${formData.paymentMethod === opt.value ? 'selected' : ''}`}
                          onClick={() => updateField('paymentMethod', opt.value)}
                        >
                          <span>{opt.icon}</span>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Section 5: Notes */}
        <section className="pr-section">
          <div className="pr-section-header">
            <div className="pr-section-icon">📝</div>
            <div className="pr-section-title">
              <h3>{t('publishRide.additionalNotes')}</h3>
              <p>{t('publishRide.anythingElse')}</p>
            </div>
          </div>
          <div className="pr-section-body">
            <textarea
              name="description"
              className="pr-textarea"
              value={formData.description}
              onChange={handleChange}
              placeholder={isOffer 
                ? t('publishRide.notesPlaceholderOffer')
                : t('publishRide.notesPlaceholderRequest')
              }
            />
          </div>
        </section>

            {/* Step 3 Navigation */}
            <div className="pr-step-nav">
              <button type="button" className="pr-step-btn pr-step-btn-back" onClick={goBack}>
                ← {t('common.back') || 'Back'}
              </button>
              <button 
                type="button" 
                className="pr-step-btn pr-step-btn-submit" 
                onClick={handlePublish}
                disabled={submitting}
              >
                {isEdit 
                  ? `✏️ ${t('publishRide.updateRide') || 'Update'}`
                  : `🚀 ${isOffer ? t('publishRide.publishRide') : t('publishRide.sendRequest')}`
                }
              </button>
            </div>
          </>
        )}
      </main>

      {/* Suggestion Modal - Show nearby offers when requesting a ride */}
      {showSuggestionModal && suggestedOffers.length > 0 && (
        <div className="pr-modal-overlay" onClick={() => setShowSuggestionModal(false)}>
          <div className="pr-suggestion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pr-suggestion-header">
              <h2>✨ {t('publishRide.nearbyOffersFound') || 'Nearby Rides Found!'}</h2>
              <button className="pr-modal-close" onClick={() => setShowSuggestionModal(false)}>×</button>
            </div>
            <div className="pr-suggestion-content">
              <p>{t('publishRide.nearbyOffersDesc') || 'We found some rides near your location. You can join one of these instead of creating a new request:'}</p>
              <div className="pr-suggested-offers">
                {suggestedOffers.slice(0, 3).map((offer) => (
                  <div key={offer.offer_id} className="pr-suggested-offer-card">
                    <div className="pr-suggested-offer-header">
                      <span className="pr-suggested-driver">{offer.driver_name || offer.name}</span>
                      <span className="pr-suggested-seats">
                        {offer.total_seats - (offer.confirmed_passengers?.reduce((sum, p) => sum + (p.passenger_count || 1), 0) || 0)}/{offer.total_seats} {t('publishRide.seatsAvailable') || 'seats'}
                      </span>
                    </div>
                    <div className="pr-suggested-offer-location">
                      {offer.vehicle_type === 'public' ? '🚌' : '🚗'} {offer.locations?.[0]?.location_address || offer.locations?.[0]?.nickname || 'Location'}
                    </div>
                    <button 
                      className="pr-btn-view-offer"
                      onClick={() => {
                        setShowSuggestionModal(false);
                        navigate(`/event/${eventCode}?highlight=${offer.offer_id}`);
                      }}
                    >
                      {t('publishRide.viewOffer') || 'View Offer'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="pr-suggestion-footer">
              <button className="pr-btn-secondary" onClick={() => setShowSuggestionModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="pr-btn-primary" onClick={proceedWithRequest}>
                {t('publishRide.createRequestAnyway') || 'Create Request Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Modal removed - direct login/register */}
    </div>
  );
}

export default PublishRidePage;